/**
 * Sub-parts for InvoiceFormLineItems to keep functions under limits.
 */

import { useEffect, useState } from 'react'

import { GST_RATE_OPTIONS, UNIT_OPTIONS, getQtyDecimalPlaces } from './invoice-form-helpers'
import { fetchCatalogUoms } from '../../../services/catalog-service'
import { formatAmount } from '../../../utils/billing-helpers'
import { getCurrencyConfig } from '../../../utils/formatters'
import {
  Input,
  NumericInput,
  Select,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui'

import type { LineItem } from './invoice-form-helpers'
import type { Account } from '../../../services/account-service'
import type { SelectOption } from '../../ui'

// The account-override column only applies to purchase-side documents — sales
// invoices keep their existing automatic revenue-account resolution.
export function isVendorInvoiceType(invoiceType: string): boolean {
  return invoiceType === 'vendor_bill' || invoiceType === 'vendor_credit_note'
}

// Human-readable UOM label: the name, with the symbol in parens when it differs.
function uomLabel(name: string | null | undefined, value: string): string {
  const trimmed = name?.trim()
  if (!trimmed) { return value }
  return trimmed.toLowerCase() === value.toLowerCase() ? trimmed : `${trimmed} (${value})`
}

export function useUomOptions(): SelectOption[] {
  const [options, setOptions] = useState<SelectOption[]>(UNIT_OPTIONS)
  useEffect(() => {
    void fetchCatalogUoms().then((uoms) => {
      if (uoms.length === 0) { return }
      // Multiple catalog UOMs can share a symbol (e.g. two "gal" rows). Dedupe by
      // value so the <Select> options carry unique keys.
      // Value stays the symbol/code (what line items store as `unit`); the label
      // shows the human-readable name, with the symbol in parens when it adds info.
      const seen = new Set<string>()
      const deduped: SelectOption[] = []
      for (const u of uoms) {
        const value = u.symbol ?? u.code
        if (!value || seen.has(value)) { continue }
        seen.add(value)
        deduped.push({ value, label: uomLabel(u.name, value) })
      }
      setOptions(deduped)
    })
  }, [])
  return options
}

interface LineItemsTableHeaderProps {
  invoiceType: string
}

export function LineItemsTableHeader({ invoiceType }: LineItemsTableHeaderProps): JSX.Element {
  return (
    <TableHeader>
      <TableRow>
        <TableHead style={{ width: 'calc(var(--el-layout-gap-md) / 2)', padding: 0 }} />
        <TableHead style={{ width: '35px', fontSize: '0.8125rem' }}>#</TableHead>
        <TableHead style={{ minWidth: '240px', fontSize: '0.8125rem' }}>Description</TableHead>
        <TableHead style={{ width: '100px', fontSize: '0.8125rem' }}>HSN/SAC</TableHead>
        <TableHead style={{ width: '90px', fontSize: '0.8125rem' }}>Unit</TableHead>
        <TableHead align="right" style={{ width: '80px', fontSize: '0.8125rem' }}>Qty</TableHead>
        <TableHead align="right" style={{ width: '100px', fontSize: '0.8125rem' }}>Rate</TableHead>
        <TableHead align="right" style={{ width: '75px', fontSize: '0.8125rem' }}>Disc %</TableHead>
        {invoiceType !== 'export' && (
          <TableHead align="right" style={{ width: '85px', fontSize: '0.8125rem' }}>GST %</TableHead>
        )}
        {invoiceType !== 'export' && (
          <TableHead align="right" style={{ width: '100px', fontSize: '0.8125rem' }}>Tax</TableHead>
        )}
        <TableHead align="right" style={{ width: '115px', fontSize: '0.8125rem' }}>Amount</TableHead>
        {isVendorInvoiceType(invoiceType) && (
          <TableHead style={{ width: '140px', fontSize: '0.8125rem' }}>Account</TableHead>
        )}
        <TableHead align="center" style={{ width: '40px' }} />
        <TableHead style={{ width: 'calc(var(--el-layout-gap-md) / 2)', padding: 0 }} />
      </TableRow>
    </TableHeader>
  )
}

interface DescriptionCellProps {
  item: LineItem
  isLoading: boolean
  onUpdate: (field: keyof LineItem, value: string) => void
  onPickItem: () => void
}

function DescriptionCell({ item, isLoading, onUpdate, onPickItem }: DescriptionCellProps): JSX.Element {
  return (
    <TableCell>
      <div className="flex items-center gap-1">
        <Input
          hideMessage
          disabled={isLoading}
          placeholder="Type / to search catalog"
          size="sm"
          style={{ fontSize: '0.8125rem' }}
          type="text"
          value={item.description}
          onChange={(e) => onUpdate('description', e.target.value)}
          onKeyDown={(e) => { if (e.key === '/' && !item.description) { e.preventDefault(); onPickItem(); } }}
        />
        <button
          className="shrink-0 p-1 rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
          disabled={isLoading}
          title="Pick from catalog"
          type="button"
          onClick={onPickItem}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
        </button>
      </div>
    </TableCell>
  )
}

interface NumericCellsProps {
  item: LineItem
  currency: string
  isLoading: boolean
  onUpdate: (field: keyof LineItem, value: string) => void
}

function QuantityRateDiscountCells({ item, currency, isLoading, onUpdate }: NumericCellsProps): JSX.Element {
  const qtyDecimalPlaces = getQtyDecimalPlaces(item.unit)
  // Rate accepts as many decimals as the currency allows (e.g. 2 for INR/USD,
  // 0 for JPY, 3 for KWD) so it matches how the amount is rounded on save.
  const rateDecimalPlaces = getCurrencyConfig(currency).decimalPlaces
  return (
    <>
      <TableCell>
        <NumericInput
          data-qty-row={item.id}
          decimalPlaces={qtyDecimalPlaces}
          disabled={isLoading}
          placeholder="0"
          size="sm"
          style={{ fontSize: '0.8125rem' }}
          value={item.quantity}
          onChange={(e) => onUpdate('quantity', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <NumericInput
          decimalPlaces={rateDecimalPlaces}
          disabled={isLoading}
          placeholder={rateDecimalPlaces > 0 ? `0.${'0'.repeat(rateDecimalPlaces)}` : '0'}
          size="sm"
          style={{ fontSize: '0.8125rem' }}
          value={item.unitPrice}
          onChange={(e) => onUpdate('unitPrice', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <NumericInput
          decimalPlaces={2}
          disabled={isLoading}
          placeholder="0"
          size="sm"
          style={{ fontSize: '0.8125rem' }}
          value={item.discountPercent}
          onChange={(e) => onUpdate('discountPercent', e.target.value)}
        />
      </TableCell>
    </>
  )
}

interface GstCellsProps {
  item: LineItem
  calculated: LineItem
  currency: string
  isLoading: boolean
  onUpdate: (field: keyof LineItem, value: string) => void
}

function GstCells({ item, calculated, currency, isLoading, onUpdate }: GstCellsProps): JSX.Element {
  return (
    <>
      <TableCell>
        <Select
          hideMessage
          className="text-[0.8125rem] min-w-[70px]"
          disabled={isLoading}
          options={GST_RATE_OPTIONS}
          size="sm"
          value={item.gstRate}
          onChange={(value) => onUpdate('gstRate', value)}
        />
      </TableCell>
      <TableCell mono align="right" style={{ fontSize: '0.8125rem' }}>
        {formatAmount(
          calculated.cgstAmount + calculated.sgstAmount + calculated.igstAmount,
          currency,
        )}
      </TableCell>
    </>
  )
}

interface UnitCellProps {
  item: LineItem
  uomOptions: SelectOption[]
  isLoading: boolean
  onUpdate: (field: keyof LineItem, value: string) => void
}

function UnitCell({ item, uomOptions, isLoading, onUpdate }: UnitCellProps): JSX.Element {
  return (
    <TableCell>
      <Select
        hideMessage
        className="text-[0.8125rem] min-w-[75px]"
        disabled={isLoading}
        options={uomOptions}
        size="sm"
        value={item.unit}
        onChange={(value) => onUpdate('unit', value)}
      />
    </TableCell>
  )
}

interface HsnCellProps {
  item: LineItem
  isLoading: boolean
  onUpdate: (field: keyof LineItem, value: string) => void
}

function HsnCell({ item, isLoading, onUpdate }: HsnCellProps): JSX.Element {
  return (
    <TableCell>
      <Input
        hideMessage
        disabled={isLoading}
        placeholder="HSN/SAC"
        size="sm"
        style={{ fontSize: '0.8125rem' }}
        type="text"
        value={(item.hsnCode || item.sacCode) ?? ''}
        onChange={(e) => {
          onUpdate('hsnCode', e.target.value)
          if (e.target.value) { onUpdate('sacCode', '') }
        }}
      />
    </TableCell>
  )
}

interface AccountCellProps {
  item: LineItem
  accounts: Account[]
  isLoading: boolean
  onPickAccount: () => void
  onClearAccount: () => void
}

// Shows the manually overridden GL account for this purchase line, if any,
// with a picker to set/change it. No override = the line resolves normally
// (item-specific account, then the generic purchase-expense fallback).
function AccountCell({ item, accounts, isLoading, onPickAccount, onClearAccount }: AccountCellProps): JSX.Element {
  const selected = item.accountId ? accounts.find(a => a.id === item.accountId) : undefined
  if (selected) {
    return (
      <TableCell>
        <div className="flex items-center gap-1">
          <span className="truncate text-[0.8125rem]" title={`${selected.code} ${selected.name}`}>
            {selected.code} {selected.name}
          </span>
          <button
            className="shrink-0 p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-muted transition-colors"
            disabled={isLoading}
            title="Clear account override"
            type="button"
            onClick={onClearAccount}
          >
            ×
          </button>
        </div>
      </TableCell>
    )
  }
  return (
    <TableCell>
      <button
        className="text-[0.8125rem] text-muted-foreground hover:text-primary transition-colors"
        disabled={isLoading}
        type="button"
        onClick={onPickAccount}
      >
        Choose…
      </button>
    </TableCell>
  )
}

interface RemoveCellProps {
  isRemoveDisabled: boolean
  onRemove: () => void
}

function RemoveCell({ isRemoveDisabled, onRemove }: RemoveCellProps): JSX.Element {
  return (
    <TableCell align="center">
      <button
        className="bg-surface border border-border text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800 disabled:text-foreground-secondary disabled:cursor-not-allowed disabled:bg-surface-secondary text-xl cursor-pointer px-1 rounded-sm transition-all leading-none"
        disabled={isRemoveDisabled}
        title="Remove item"
        type="button"
        onClick={onRemove}
      >
        ×
      </button>
    </TableCell>
  )
}

export interface LineItemRowProps {
  item: LineItem
  calculated: LineItem
  index: number
  invoiceType: string
  currency: string
  isLoading: boolean
  isRemoveDisabled: boolean
  uomOptions: SelectOption[]
  accounts: Account[]
  onUpdate: (field: keyof LineItem, value: string) => void
  onRemove: () => void
  onPickItem: () => void
  onPickAccount: () => void
}

export function LineItemRow(props: LineItemRowProps): JSX.Element {
  const {
    item, calculated, index, invoiceType, currency, isLoading, isRemoveDisabled, uomOptions, accounts,
    onUpdate, onRemove, onPickItem, onPickAccount,
  } = props
  return (
    <TableRow>
      <TableCell style={{ width: 'calc(var(--el-layout-gap-md) / 2)', padding: 0 }} />
      <TableCell style={{ fontSize: '0.8125rem' }}>{index + 1}</TableCell>
      <DescriptionCell isLoading={isLoading} item={item} onPickItem={onPickItem} onUpdate={onUpdate} />
      <HsnCell isLoading={isLoading} item={item} onUpdate={onUpdate} />
      <UnitCell isLoading={isLoading} item={item} uomOptions={uomOptions} onUpdate={onUpdate} />
      <QuantityRateDiscountCells currency={currency} isLoading={isLoading} item={item} onUpdate={onUpdate} />
      {invoiceType !== 'export' && (
        <GstCells calculated={calculated} currency={currency} isLoading={isLoading} item={item} onUpdate={onUpdate} />
      )}
      <TableCell mono align="right" style={{ fontSize: '0.8125rem' }}>
        {formatAmount(item.totalAmount, currency)}
      </TableCell>
      {isVendorInvoiceType(invoiceType) && (
        <AccountCell
          accounts={accounts} isLoading={isLoading} item={item}
          onClearAccount={() => onUpdate('accountId', '')}
          onPickAccount={onPickAccount}
        />
      )}
      <RemoveCell isRemoveDisabled={isRemoveDisabled} onRemove={onRemove} />
      <TableCell style={{ width: 'calc(var(--el-layout-gap-md) / 2)', padding: 0 }} />
    </TableRow>
  )
}
